import dayjs from "dayjs";
import { parseCsvDateToIsoOptional } from "./csvDate";
import { getDueDatePart } from "./dueDateTime";

/** URL query value when list is filtered to upcoming earnings. */
export const STOCK_EARNINGS_UPCOMING_PARAM = "earningsUpcoming";
export const STOCK_EARNINGS_UPCOMING_VALUE = "1";

/** Default window: earnings on or after today through the next 30 calendar days. */
export const STOCK_EARNINGS_UPCOMING_DAYS = 30;

/**
 * Calendar YYYY-MM-DD for stock earnings_date (UTC date-only safe).
 * @param {unknown} earningsDate
 * @returns {string}
 */
export function getStockEarningsCalendarYmd(earningsDate) {
  const fromDue = getDueDatePart(earningsDate);
  if (fromDue) return fromDue;
  const raw = earningsDate != null ? String(earningsDate).trim() : "";
  if (!raw) return "";
  const parsed = parseCsvDateToIsoOptional(raw);
  return parsed.ok && parsed.iso ? parsed.iso : "";
}

/**
 * Holdings use `earnings_date`; buy recommendations use Yahoo UI label `Earnings Date`.
 * @param {{ earnings_date?: unknown, 'Earnings Date'?: unknown }|null|undefined} row
 * @returns {unknown}
 */
export function getStockEarningsDateFromRow(row) {
  if (!row) return null;
  return row.earnings_date ?? row["Earnings Date"] ?? null;
}

/**
 * @param {unknown} earningsDate
 * @param {{ days?: number, today?: import('dayjs').Dayjs }} [options]
 * @returns {boolean}
 */
export function isStockEarningsUpcomingWithinDays(earningsDate, options = {}) {
  const days =
    Number.isFinite(options.days) && options.days >= 0 ? options.days : STOCK_EARNINGS_UPCOMING_DAYS;
  const ymd = getStockEarningsCalendarYmd(earningsDate);
  if (!ymd) return false;
  const target = dayjs(ymd, "YYYY-MM-DD", true);
  if (!target.isValid()) return false;
  const today = (options.today ?? dayjs()).startOf("day");
  const end = today.add(days, "day");
  const t = target.startOf("day");
  return !t.isBefore(today) && !t.isAfter(end);
}

/**
 * @param {Array<{ earnings_date?: unknown }>} stocks
 * @param {{ days?: number, today?: import('dayjs').Dayjs }} [options]
 * @returns {Array}
 */
export function filterStocksWithUpcomingEarnings(stocks, options = {}) {
  return (stocks || []).filter((s) => isStockEarningsUpcomingWithinDays(getStockEarningsDateFromRow(s), options));
}

/**
 * @param {Array<{ earnings_date?: unknown }>} stocks
 * @param {{ days?: number, today?: import('dayjs').Dayjs }} [options]
 * @returns {number}
 */
export function countStocksWithUpcomingEarnings(stocks, options = {}) {
  return filterStocksWithUpcomingEarnings(stocks, options).length;
}

/**
 * @param {string|null|undefined} paramValue
 * @returns {boolean}
 */
export function isStockEarningsUpcomingUrlActive(paramValue) {
  return paramValue === STOCK_EARNINGS_UPCOMING_VALUE;
}
